import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
    BoltIcon,
    TableCellsIcon,
    CheckBadgeIcon,
    ExclamationCircleIcon,
    ArrowPathIcon,
    TicketIcon,
    Squares2X2Icon
} from '@heroicons/react/24/outline';
import i18n from '../i18n';

const API_URL = '/api/v1';

export default function MeterPortal() {
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [selectedDevice, setSelectedDevice] = useState(null);
    const [formData, setFormData] = useState({
        startDate: '',
        endDate: '',
        totalMwh: '',
        selfConsumptionMwh: '',
        gridInjectedMwh: '',
        isSplit: false
    });
    const [success, setSuccess] = useState(null);
    const [error, setError] = useState(null);

    const fetchData = async () => {
        setLoading(true);
        try {
            const email = localStorage.getItem('email') || 'engineer@onee.ma';
            const role = localStorage.getItem('role') || 'GRID_OPERATOR';
            const headers = { 'X-User-Email': email, 'X-User-Role': role };

            const res = await axios.get(`${API_URL}/devices`, { headers });
            setDevices(res.data);
        } catch (err) {
            console.error('Error fetching portal data:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleSelectDevice = (device) => {
        setSelectedDevice(device);
        setFormData({ ...formData, isSplit: device.is_autoproduction });
        setSuccess(null);
        setError(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);

        try {
            const email = localStorage.getItem('userEmail') || 'engineer@onee.ma';
            const role = localStorage.getItem('userRole') || 'GRID_OPERATOR';
            const headers = { 'X-User-Email': email, 'X-User-Role': role };

            // Scenario 1: Validate Split Balance
            if (formData.isSplit) {
                const total = parseFloat(formData.selfConsumptionMwh) + parseFloat(formData.gridInjectedMwh);
                if (Math.abs(total - parseFloat(formData.totalMwh)) > 0.001) {
                    throw new Error(`Volume Balance Mismatch: Total (${formData.totalMwh}) must equal Self-Cons (${formData.selfConsumptionMwh}) + Grid (${formData.gridInjectedMwh})`);
                }
            }

            const payload = {
                energy_source_code: selectedDevice.energy_source_code,
                technology_code: selectedDevice.technology_code,
                energy_medium_code: 'E',
                capacity_mw: parseFloat(selectedDevice.capacity_mw),
                output_mwh: parseFloat(formData.totalMwh),
                self_consumption_mwh: formData.isSplit ? parseFloat(formData.selfConsumptionMwh) : null,
                grid_injected_mwh: formData.isSplit ? parseFloat(formData.gridInjectedMwh) : null,
                production_start: new Date(formData.startDate).toISOString(),
                production_end: new Date(formData.endDate).toISOString(),
                production_device_id: selectedDevice.id,
                account_holder_id: selectedDevice.owner_id,
                usage_category: formData.isSplit ? "SELF_CONSUMPTION" : "GRID_INJECTED",
                purpose: 'GRID_OPERATOR_VERIFIED'
            };

            await axios.post(`${API_URL}/certificates`, payload, { headers });
            setSuccess(i18n.t('status_pending_issuance') + ": Logged for Ministry Final Approval.");
            setFormData({ startDate: '', endDate: '', totalMwh: '', selfConsumptionMwh: '', gridInjectedMwh: '', isSplit: false });
        } catch (err) {
            setError(err.message || err.response?.data?.detail || "Verification rejection.");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return (
        <div className="flex items-center justify-center py-40">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
        </div>
    );

    return (
        <div className="space-y-8 animate-fade-in max-w-6xl mx-auto py-6">
            <div className="flex items-center justify-between border-b border-gray-200 pb-6">
                <div>
                    <h1 className="text-3xl font-black text-gray-800 tracking-tighter uppercase">{i18n.t('metering_portal') || 'Metering Provider Portal'}</h1>
                    <p className="text-[10px] text-gray-400 font-black uppercase tracking-[0.2em] mt-1">{i18n.t('grid_operator_verif') || 'Grid Operator Verification Interface (ONEE/DSO)'}</p>
                </div>
                <div className="flex bg-indigo-50 border border-indigo-100 p-4 rounded-sm items-center gap-3">
                    <CheckBadgeIcon className="w-6 h-6 text-indigo-600" />
                    <div>
                        <p className="text-[10px] font-black text-indigo-900 uppercase">Trusted Domain Verified</p>
                        <p className="text-[9px] text-indigo-700/60 font-bold">Standard: Moroccan Decree 2.24.761</p>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Device List Section */}
                <div className="lg:col-span-2 space-y-6">
                    <div className="glass-card bg-white border border-gray-200 shadow-xl overflow-hidden">
                        <div className="p-6 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                            <h3 className="text-xs font-black text-gray-800 uppercase tracking-widest flex items-center gap-2">
                                <BoltIcon className="w-4 h-4 text-indigo-600" /> Managed Assets Portfolio
                            </h3>
                            <button onClick={fetchData} className="text-gray-400 hover:text-indigo-600 transition-colors">
                                <ArrowPathIcon className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="overflow-x-auto text-[11px]">
                            <table className="w-full">
                                <thead>
                                    <tr className="bg-gray-50/50 text-gray-400 font-black uppercase tracking-widest border-b border-gray-100">
                                        <th className="px-6 py-4 text-left">GSRN / Asset</th>
                                        <th className="px-6 py-4 text-left">Jurisdiction</th>
                                        <th className="px-6 py-4 text-left">Regulatory Flag</th>
                                        <th className="px-6 py-4 text-right">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50">
                                    {devices.map(d => (
                                        <tr
                                            key={d.id}
                                            onClick={() => handleSelectDevice(d)}
                                            className={`cursor-pointer transition-all ${selectedDevice?.id === d.id ? 'bg-indigo-50 border-l-4 border-l-indigo-600' : 'hover:bg-gray-50'}`}
                                        >
                                            <td className="px-6 py-4">
                                                <p className="font-black text-gray-800">{d.device_name}</p>
                                                <p className="font-mono text-[9px] text-indigo-600">{d.gsrn}</p>
                                            </td>
                                            <td className="px-6 py-4 font-bold text-gray-500">{d.city || 'National'}</td>
                                            <td className="px-6 py-4">
                                                <div className="flex gap-2">
                                                    {d.is_autoproduction && <span className="px-2 py-0.5 rounded-[2px] text-[8px] font-black uppercase bg-amber-50 border border-amber-200 text-amber-700">{i18n.t('law_82_21')}</span>}
                                                    {d.h2_ready && <span className="px-2 py-0.5 rounded-[2px] text-[8px] font-black uppercase bg-indigo-50 border border-indigo-200 text-indigo-700">H2-OFFRE MAROC</span>}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <span className="text-[10px] font-black text-emerald-600 uppercase tracking-tighter">{d.status}</span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Meter Reading Form */}
                <div className="lg:col-span-1">
                    {selectedDevice ? (
                        <div className="glass-card bg-white border border-gray-200 shadow-2xl p-8 animate-slide-in-right sticky top-6">
                            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100">
                                <TicketIcon className="w-6 h-6 text-indigo-600" />
                                <h3 className="text-lg font-black text-gray-800 uppercase tracking-tight">Record Meter Reading</h3>
                            </div>

                            {success && (
                                <div className="mb-6 p-4 bg-emerald-50 border border-emerald-100 text-emerald-700 flex items-center gap-3 animate-fade-in shadow-sm">
                                    <CheckBadgeIcon className="w-5 h-5 flex-shrink-0" />
                                    <p className="text-[10px] font-black uppercase tracking-widest leading-tight">{success}</p>
                                </div>
                            )}

                            {error && (
                                <div className="mb-6 p-4 bg-red-50 border border-red-100 text-red-600 flex items-center gap-3 animate-shake shadow-sm">
                                    <ExclamationCircleIcon className="w-5 h-5 flex-shrink-0" />
                                    <p className="text-[10px] font-black uppercase tracking-widest">{error}</p>
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest ml-1">Period Start</label>
                                        <input required name="startDate" value={formData.startDate} onChange={(e) => setFormData({ ...formData, startDate: e.target.value })} type="date" className="w-full p-4 bg-gray-50 border border-gray-200 outline-none focus:border-indigo-600 transition-all text-xs font-bold text-gray-700 rounded-none" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest ml-1">Period End</label>
                                        <input required name="endDate" value={formData.endDate} onChange={(e) => setFormData({ ...formData, endDate: e.target.value })} type="date" className="w-full p-4 bg-gray-50 border border-gray-200 outline-none focus:border-indigo-600 transition-all text-xs font-bold text-gray-700 rounded-none" />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest ml-1">Total Metered Output (MWh)</label>
                                    <div className="relative">
                                        <input required name="totalMwh" value={formData.totalMwh} onChange={(e) => setFormData({ ...formData, totalMwh: e.target.value })} type="number" step="0.001" placeholder="0.000" className="w-full p-6 bg-indigo-50/30 border border-gray-200 outline-none focus:border-indigo-600 transition-all text-sm font-black font-mono text-indigo-700" />
                                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-300 font-bold text-[10px] uppercase">MWh</span>
                                    </div>
                                </div>

                                {selectedDevice.is_autoproduction && (
                                    <div className="p-6 bg-amber-50/50 border-2 border-dashed border-amber-200 space-y-4 animate-fade-in">
                                        <p className="text-[10px] font-black text-amber-800 uppercase tracking-widest flex items-center gap-2">
                                            <Squares2X2Icon className="w-4 h-4" /> Law 82-21 Dual-Allocation
                                        </p>
                                        <div className="grid grid-cols-1 gap-4">
                                            <div className="space-y-2">
                                                <label className="text-[9px] font-black text-amber-600 uppercase tracking-widest">{i18n.t('usage_self_consumption')}</label>
                                                <input required name="selfCons" value={formData.selfConsumptionMwh} onChange={(e) => setFormData({ ...formData, selfConsumptionMwh: e.target.value })} type="number" step="0.001" className="w-full p-3 bg-white border border-amber-200 text-xs font-bold font-mono outline-none focus:border-amber-500" />
                                            </div>
                                            <div className="space-y-2">
                                                <label className="text-[9px] font-black text-amber-600 uppercase tracking-widest">{i18n.t('usage_grid_injected')}</label>
                                                <input required name="gridInjected" value={formData.gridInjectedMwh} onChange={(e) => setFormData({ ...formData, gridInjectedMwh: e.target.value })} type="number" step="0.001" className="w-full p-3 bg-white border border-amber-200 text-xs font-bold font-mono outline-none focus:border-amber-500" />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <button type="submit" disabled={submitting} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-5 rounded-sm text-[11px] font-black uppercase tracking-widest shadow-2xl transition-all disabled:opacity-50">
                                    {submitting ? 'Authenticating with Hub...' : 'Certify Measurement Readings'}
                                </button>
                            </form>
                        </div>
                    ) : (
                        <div className="border-2 border-dashed border-gray-100 h-[400px] flex items-center justify-center p-12 text-center">
                            <div>
                                <BoltIcon className="w-8 h-8 text-gray-100 mx-auto mb-4" />
                                <p className="text-[10px] font-black text-gray-300 uppercase tracking-[0.3em]">Select asset to begin verification</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
